import { z } from 'zod';
import {
  DUTY_ANALYTICS_DRILL_GRAINS,
  DUTY_ANALYTICS_DRILL_LIMIT,
  DUTY_ANALYTICS_LAYOUTS,
  DUTY_ANALYTICS_METRICS,
  DUTY_ANALYTICS_RANGES,
} from '../constants/dutyAnalytics.constant';
import { analyticsCountSchema, analyticsSection } from './analytics.schema';
import { calendarDateSchema } from './arrangement.schema';
import { objectIdSchema, PAGINATION } from './common.schema';
import { dashboardTileColumnSchema } from './dashboard.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2] THE DUTY ANALYTICS PAGE'S WIRE FORMAT — `GET /duty-analytics` and its drill.
 *
 *  ⚠️ EVERY FIGURE IS AN INTEGER COUNT, AND THE SCHEMA IS WHAT ENFORCES IT (§2d). A ratio, a
 *  percentage or a score cannot pass `analyticsCountSchema`, and no key here names one.
 *
 *  ⚠️ THREE SECTIONS, EACH `{ data } | { failure }` — Usage analytics' isolation: a section that
 *  could not be computed says so and never arrives as zeros. If FILL fails the other two fail with
 *  it, because the shares are what decide which units a division may see.
 *
 *  ⚠️ ROWS ARE CELLS, IDENTITIES ARE SEPARATE. `rows`/`units` list who appears; every section's
 *  `rows`/`units` carry the same `{ orgUnitId, divisionId }` keys, so the web joins a row's seven
 *  columns across three sections by key — and a section that failed leaves its columns empty
 *  without hiding the row.
 *
 *  ⚠️ `commissionerate` IS U2'S DELIBERATE DISCLOSURE: on the DIVISION layout, the whole
 *  commissionerate's totals and nothing else — no row, no id, no name, and no drill parameter can
 *  reach them. With two divisions, subtraction reveals the other; that cost was accepted.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const count = analyticsCountSchema;
const section = analyticsSection;

/** `?range=7` arrives as a string. Measured by the schema test: `z.enum` over numbers accepts nothing. */
export const dutyAnalyticsRangeSchema = z.coerce.number().pipe(z.literal(DUTY_ANALYTICS_RANGES));

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [amended P7C.1 §4 · §5] THE QUERY: A WINDOW, AN OPTIONAL NARROWING, AND WHOSE PAGE THIS IS.
 *
 *  ⚠️ THE WINDOW IS A PRESET (`range`) OR A CUSTOM PAIR (`dateFrom`/`dateTo` — the repo's request naming, the
 *  dashboard's and the Audit log's), NEVER BOTH. `range` carries NO schema default: the service applies
 *  `DUTY_ANALYTICS_DEFAULT_RANGE`, because a default here would put 7 beside every pair. Whether a pair is a
 *  window the page may read (reversed, a future end, more than thirty days) is the SERVICE's refusal, so its
 *  sentence is the 422's message; this schema refuses only a SHAPE — half a pair, or a pair beside a range.
 *
 *  ⚠️ ONE NAMED PAIR RULE, APPLIED EXPLICITLY TO BOTH THE PAGE AND THE DRILL. Never left to `.extend()`, which
 *  in zod 4.4.3 silently copies a refined object's checks: the two schemas are built from one shape, each with
 *  its own `.superRefine(windowPairRule)`.
 *
 *  ⚠️ `asDivisionId` READS A DIVISION'S OWN PAGE, AS THAT DIVISION — commissionerate callers only (§4). On a
 *  division's page any value is the one 404, its own included; the server resolves it by POSITION.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const dutyAnalyticsQueryShape = {
  range: dutyAnalyticsRangeSchema.optional(),
  dateFrom: calendarDateSchema.optional(),
  dateTo: calendarDateSchema.optional(),
  /** COMMISSIONERATE layout only: narrows `units` to one division. On the DIVISION layout it is a 404. */
  divisionId: objectIdSchema.optional(),
  /** [P7C.1 §4] The division whose own page to read, as that division. A commissionerate caller's alone. */
  asDivisionId: objectIdSchema.optional(),
};

/** The words of a refused SHAPE; the window's own refusals are `dutyAnalyticsWindowRefusal`'s, in the service. */
const HALF_PAIR = 'Send both dates of a custom range, or neither.';
const PAIR_WITH_RANGE = 'Send a range or a pair of dates, not both.';

const windowPairRule = (
  query: { range?: unknown; dateFrom?: unknown; dateTo?: unknown },
  ctx: z.RefinementCtx,
): void => {
  if (query.dateFrom === undefined && query.dateTo !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['dateFrom'], message: HALF_PAIR });
  } else if (query.dateFrom !== undefined && query.dateTo === undefined) {
    ctx.addIssue({ code: 'custom', path: ['dateTo'], message: HALF_PAIR });
  } else if (query.dateFrom !== undefined && query.range !== undefined) {
    ctx.addIssue({ code: 'custom', path: ['range'], message: PAIR_WITH_RANGE });
  }
};

export const dutyAnalyticsQuerySchema = z.object(dutyAnalyticsQueryShape).superRefine(windowPairRule);

/**
 * The server's echo of the window it read. A preset: trailing IST days, `to` is today. [amended P7C.1 §5] A
 * custom pair: `range` is null, and `to` is today or earlier — never later.
 */
export const dutyAnalyticsWindowSchema = z.object({
  from: calendarDateSchema,
  to: calendarDateSchema,
  range: z.literal(DUTY_ANALYTICS_RANGES).nullable(),
});

/**
 * Who a row is. `divisionId` is set on a COMMISSIONERATE unit row (the division it was asked
 * through) and null everywhere else. `isCommissionerate` marks the root's own row (force asked of
 * the commissionerate itself); `isDivisionItself` marks a division's own kept work.
 *
 * [P7C.1 §4] `isDivisionLevel` — the unit is a depth-1 division by POSITION (`raiserLevelOf`), so its own page
 * exists. The web offers a division's name as a link on this alone: a battalion used as an allocation's
 * division is labelled DIVISION at depth 2, and a link offered on the label would always answer 404.
 */
export const dutyAnalyticsIdentitySchema = z.object({
  orgUnitId: objectIdSchema,
  orgUnit: orgUnitSummarySchema.nullable(),
  divisionId: objectIdSchema.nullable(),
  isDivisionItself: z.boolean(),
  isCommissionerate: z.boolean(),
  isDivisionLevel: z.boolean(),
});

const keyed = { orgUnitId: objectIdSchema, divisionId: objectIdSchema.nullable() };

/** Duties asked (people only), named, short-falls declared (a reasoned close short), shares left open (ended, not settled). */
export const dutyAnalyticsFillFiguresSchema = z.object({
  asked: count,
  named: count,
  declaredShort: count,
  leftOpen: count,
});

/** Positions handed down to a unit beneath, and how many distinct units received them. */
export const dutyAnalyticsDelegationFiguresSchema = z.object({ delegated: count, recipients: count });

/** Reached and absent force subjects; their taps split by who entered them (U1). Never a command count. */
export const dutyAnalyticsAttendanceFiguresSchema = z.object({
  reached: count,
  absent: count,
  markedOnBehalf: count,
  markedSelf: count,
});

const dutyAnalyticsPickSchema = z.object({
  orgUnitId: objectIdSchema,
  orgUnit: orgUnitSummarySchema.nullable(),
  asked: count,
  named: count,
});

/**
 * ⚠️ `BEST_MAPPING.pick` IS NULL WHEN NOBODY QUALIFYING NAMED ANYONE — never "0 of 212 named"
 * crowned best. `floor` states the U4 rule in counts: rows asked for at least `askedAtLeast`
 * (the ceiling of the median among `considered` rows asked for anything), `qualifying` of them.
 */
export const dutyAnalyticsCardSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('MOST_ASKED'), pick: dutyAnalyticsPickSchema.nullable() }),
  z.object({
    kind: z.literal('BEST_MAPPING'),
    pick: dutyAnalyticsPickSchema.nullable(),
    floor: z.object({ askedAtLeast: count, qualifying: count, considered: count }).nullable(),
  }),
  z.object({ kind: z.literal('ASKED_AND_NAMED'), asked: count, named: count }),
  z.object({ kind: z.literal('OPEN_AND_DECLARED'), leftOpen: count, declaredShort: count }),
]);

/**
 * ⚠️ GENERIC OVER THE SHAPE, NEVER OVER `z.ZodObject` ITSELF — measured by declaration emit: a
 * parameter typed `F extends z.ZodObject` makes `figures.extend(keyed)` infer as an object of
 * UNKNOWN keys, so every section's rows reached both readers as `Record<string, unknown>` and each
 * needed a cast. Over `z.ZodObject<S>` the row infers as the figures plus the two keys.
 */
const sectionData = <S extends z.ZodRawShape>(figures: z.ZodObject<S>) => {
  const row = figures.extend(keyed);
  return z.object({ rows: z.array(row), units: z.array(row).nullable(), total: figures });
};

export const dutyAnalyticsSchema = z.object({
  window: dutyAnalyticsWindowSchema,
  layout: z.enum(DUTY_ANALYTICS_LAYOUTS),
  /**
   * The unit whose page this is: the acting unit, null for a GLOBAL scope (a super admin), whose view is the
   * commissionerate — [amended P7C.1 §4] or the division named by `asDivisionId`.
   */
  viewer: orgUnitSummarySchema.nullable(),
  /** COMMISSIONERATE only: the division `units` is narrowed to, echoed. */
  narrowedTo: orgUnitSummarySchema.nullable(),
  /** COMMISSIONERATE: one row per division, asked desc. DIVISION: one row per unit, its own kept work included. */
  rows: z.array(dutyAnalyticsIdentitySchema),
  /** COMMISSIONERATE: every (division, unit), or the narrowed division's. DIVISION: null. */
  units: z.array(dutyAnalyticsIdentitySchema).nullable(),
  fill: section(sectionData(dutyAnalyticsFillFiguresSchema).extend({ cards: z.array(dutyAnalyticsCardSchema) })),
  delegation: section(sectionData(dutyAnalyticsDelegationFiguresSchema)),
  attendance: section(sectionData(dutyAnalyticsAttendanceFiguresSchema)),
  /** ⚠️ U2 — DIVISION layout only, totals only, never drillable. */
  commissionerate: z
    .object({
      fill: section(dutyAnalyticsFillFiguresSchema),
      delegation: section(dutyAnalyticsDelegationFiguresSchema),
      attendance: section(dutyAnalyticsAttendanceFiguresSchema),
    })
    .nullable(),
  generatedAt: z.iso.datetime(),
});

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DRILL — `GET /duty-analytics/rows` (§2j). The same `(divisionId?, orgUnitId?)` a page cell
 *  is built from, plus the metric.
 *
 *  ⚠️ ROWS ARE THE DASHBOARD TILE DRILL'S OWN SHAPE (`dashboardTileColumnSchema` / `RowSchema`),
 *  per-cell `hrefs` included, so the table renderer is one the product already has. Every row
 *  carries `cells.figure` — its contribution — and `figure` is the sum over ALL pages, which by
 *  construction equals the page cell.
 *
 *  ⚠️ NO OFFICER IS NAMED ON ANY GRAIN. Attendance and markings drill to DUTIES with counts —
 *  a per-officer attendance record is the question §2h asks to report, not to build.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const dutyAnalyticsRowsQuerySchema = z
  .object({
    ...dutyAnalyticsQueryShape,
    metric: z.enum(DUTY_ANALYTICS_METRICS),
    orgUnitId: objectIdSchema.optional(),
    page: z.coerce.number().int().min(1).default(PAGINATION.DEFAULT_PAGE),
    limit: z.coerce.number().int().min(1).max(PAGINATION.MAX_LIMIT).default(DUTY_ANALYTICS_DRILL_LIMIT),
  })
  .superRefine(windowPairRule);

/** The keys a drill page carries beside `paginated()`'s five — `arrangementListPageExtrasSchema`'s pattern. */
export const dutyAnalyticsRowsPageExtrasSchema = z.object({
  metric: z.enum(DUTY_ANALYTICS_METRICS),
  grain: z.enum(DUTY_ANALYTICS_DRILL_GRAINS),
  window: dutyAnalyticsWindowSchema,
  layout: z.enum(DUTY_ANALYTICS_LAYOUTS),
  cell: z.object({
    divisionId: objectIdSchema.nullable(),
    orgUnitId: objectIdSchema.nullable(),
    orgUnit: orgUnitSummarySchema.nullable(),
  }),
  figure: count,
  columns: z.array(dashboardTileColumnSchema),
});
